import React from 'react';
import PropTypes from 'prop-types';
import {locationShape, routerShape, withRouter, Link} from 'react-router';
import {action, computed, observable, reaction} from 'mobx';
import {observer, inject} from 'mobx-react';
import autobind from 'autobind-decorator';
import moment from 'moment-timezone';
import * as Sentry from '@sentry/react';
import loadable from '@loadable/component';

import isValidException from 'utils/isValidException';
import {withConfig, withI18n} from 'spa/Components/Context';
import {CheckboxGroup, Divider, Form, FormHandler, InputField, PasswordField} from 'spa/Components/Form';
import Button from '@urbanmassage/component-library/lib/Button/Button';
import CitySelect from 'spa/Components/CitySelect';
import LoginWithFacebook from './LoginWithFacebook';

import './signup-form.less';

const VerifyAccountRequest = loadable(() => import('./VerifyAccountRequest'));
const PhoneNumberInputField = loadable(() => import('spa/Components/Form/PhoneNumberInputField'));

@withRouter
@inject(({store: {notifications, session, tracker}}) => (
  {notifications, session, tracker}
))
@observer
@withConfig
@withI18n
class SignupForm extends React.Component {
  static propTypes = {
    campaignSignupError: PropTypes.func,
    customCheckboxlLabel: PropTypes.string,
    customEmail: PropTypes.string,
    customName: PropTypes.string,
    customSignup: PropTypes.func,
    discountId: PropTypes.string,
    hideAcceptMarketingComms: PropTypes.bool,
    hideFacebookSignUp: PropTypes.bool,
    location: locationShape.isRequired,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
      dismiss: PropTypes.func.isRequired,
    }).isRequired,
    onSignup: PropTypes.func,
    onSignupVoucherSuccess: PropTypes.func,
    router: routerShape.isRequired,
    selectedCity: PropTypes.string,
    session: PropTypes.shape({
      signup: PropTypes.func.isRequired,
      isLoading: PropTypes.bool.isRequired,
      isLoggedIn: PropTypes.bool.isRequired,
      isVerificationChallengeRequired: PropTypes.bool.isRequired,
      error: PropTypes.any,
      signupResponse: PropTypes.any,
      user: PropTypes.any,
      resetSignup: PropTypes.func.isRequired,
      resetError: PropTypes.func.isRequired,
    }).isRequired,
    showVoucherField: PropTypes.bool,
    signupCampaign_id: PropTypes.string,
    signupId: PropTypes.string,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
  };


  static contextTypes = {
    cityId: PropTypes.string,
  };

  static defaultProps = {
    showVoucherField: false,
    signupCampaign_id: undefined,
    trackingCategory: 'Login',
  }

  UNSAFE_componentWillMount() {
    this.props.session.resetError();

    const {customCheckboxlLabel, location} = this.props;
    const {query} = location;

    if (query.code) {
      this.voucherCodeValue = query.code;
    }

    const {config, i18n} = this.context;

    this.newsletterCheckbox = [
      {
        checked: false,
        label: customCheckboxlLabel || i18n._x('I don\'t want a 10%% discount on my second massage*, or to receive exclusive offers, news and competitions. (*valid if first Urban treatment is massage)', 'Signup Form'),
        name: 'newsletter',
        value: 'newsletter',
      },
    ];

    this.termsCheckbox = [
      {
        value: 'acceptTermsConditions',
        label: i18n.__react('Yes, I agree to the %(termsLink)s and the %(privacyLink)s of Urban.', {
          termsLink: <Link className="link" to={`/${i18n.lc}/legal`} target="_blank" rel="noopener noreferrer">{i18n._x('Terms and Conditions', 'Signup form link text')}</Link>,
          privacyLink: <Link className="link" to={`/${i18n.lc}/privacy-policy`} target="_blank" rel="noopener noreferrer">{i18n._x('Privacy Policy', 'Signup form link text')}</Link>,
        }, 'Signup form Austria accept terms'),
        name: 'acceptTermsConditions',
      },
    ];

    this.passwordMinLength = config.getValue('locale.password-min-length', i18n.umRegion) || 6;
    this.showAcceptTC = config.getValue('compliance.user-show-accept-latest-terms-and-conditions', i18n.umRegion);
    this.showAcceptMarketing = config.getValue('compliance.user-show-accept-marketing-communications', i18n.umRegion);
  }


  componentDidMount() {
    const {campaignSignupError, notifications, trackingCategory} = this.props;
    this.listeners = [
      reaction(
        () => this.props.session.error,
        (error) => {
          if (error) {
            if (error.json && error.json.code === 'ACCOUNT_ALREADY_EXISTS_WITH_EMAIL_ERROR' && !!campaignSignupError) {
              campaignSignupError('signup-message', 'error', error.json);
            } else {
              if (error.json && error.json.code === 'ACCOUNT_VERIFY_CHALLENGE') {
                this.props.tracker.track('Account verification required', {
                  category: trackingCategory,
                });

                return;
              }

              notifications.add({
                id: 'signup-message',
                type: 'error',
                title: this.context.i18n.__error(error),
              });

              const {i18n} = this.context;

              if (!i18n.isKnownError(error)) {
                this.props.tracker.track('Unknown error occurred', {
                  category: trackingCategory,
                  error,
                });
                // This is unexpected so record it in Sentry
                if (isValidException(error)) {
                  Sentry.captureException(error);
                } else {
                  Sentry.captureMessage('Signup Form, session error');
                }
              }
            }
          } else {
            notifications.dismiss('signup-message');
          }
        },
        {
          fireImmediately: true,
          name: 'Display Signup Error Message',
        },
      ),
    ];
  }

  componentWillUnmount() {
    this.props.session.resetSignup();
    while (this.listeners.length) { this.listeners.pop()(); }
  }

  @observable acceptTermsError = false;
  @observable newsletterCheckbox;
  @observable signupState = {
    password: '',
    passwordConf: '',
  };

  @autobind onReceiveFacebookLogin() {
    const {
      onSignup, onSignupVoucherSuccess, signupCampaign_id,
    } = this.props;
    if (this.voucherValue && onSignupVoucherSuccess) {
      onSignupVoucherSuccess(this.voucherValue);
    }

    if (signupCampaign_id && onSignup) {
      onSignup(undefined, undefined, signupCampaign_id);
    }
  }

  @autobind
  @action('onBlurVoucher - SignupForm component')
  onBlurVoucher(event) {
    if (this.voucherInput.input.value.replace(/\s/g, '').length) {
      this.voucherValue = this.voucherInput.input.value;
    }
  }

  @autobind
  @action('onChangeCommsOptIn - SignupForm component')
  onChangeCommsOptIn(event) {
    const { newsletterCheckbox } = this;
    if (newsletterCheckbox && newsletterCheckbox[0]) {
      newsletterCheckbox[0].checked = event.target.checked;
    }
  }

  @autobind onReceiveForm(state) {
    const {showAcceptTC} = this;
    const {customSignup, discountId, signupId} = this.props;
    const {
      acceptTermsConditions,
      city,
      email,
      'given-name': givenName,
      'family-name': familyName,
      name,
      password,
      signupCampaign_id,
      telephone,
    } = state.body;

    if (showAcceptTC && !acceptTermsConditions) {
      this.acceptTermsError = true;

      return null;
    }

    const signupData = {
      email,
      homeCity_id: city,
      meta: {},
      name: givenName ? `${givenName} ${familyName}` : name,
      password,
      signupCampaign_id,
      telephone,
    };

    signupData.meta.acceptedMarketingOptInAt = !state.body.newsletter ? moment().toISOString() : null;
    signupData.meta.autoOptedInAt = !state.body.newsletter ? moment().toISOString() : null;

    if (signupId) {
      signupData.meta.signupId = signupId;
    }

    if (discountId) {
      signupData.meta.discountScheme_id = discountId;
    }

    if (customSignup) {
      customSignup(signupData);
    } else {
      this.props.session.signup(signupData)
        .then((response) => {
          this.props.tracker.track('Created Account', {
            source: 'email',
          });

          this.onSignupSuccess(response.user.id);
        }, (error) => {
          if (error.json.code === 'ACCOUNT_VERIFY_CHALLENGE') {
            this.props.tracker.track('Created Account', {
              source: 'email',
            });
          }
          this.props.tracker.track('Signup Error', {
            category: this.props.trackingCategory,
            label: error,
          });
        });
    }
  }

  @autobind
  @action('onSignupSuccess - SignupForm')
  onSignupSuccess(userId) {
    const {
      discountId, onSignup, onSignupVoucherSuccess, signupId,
    } = this.props;

    if (!onSignup) {
      const data = {
        category: this.props.trackingCategory,
        label: userId,
      };
      if (signupId) {
        data.signupId = signupId;
      }

      if (discountId) {
        data.discountScheme_id = discountId;
      }

      this.props.tracker.track('Signup Success', data);
    } else {
      // Nowhere uses data from here now
      onSignup();
    }

    if (this.voucherValue && onSignupVoucherSuccess) {
      // Gifts
      onSignupVoucherSuccess(this.voucherValue);
    }
  }

  @autobind onValidationFail() {
    this.props.tracker.track('Signup Form Validation Error', {
      category: this.props.trackingCategory,
    });
  }

  @autobind @action onChange(event) {
    this.signupState[event.currentTarget.name] = event.currentTarget.value;
  }

  @autobind voucherInputRef(input) {
    this.voucherInput = input;
  }

  @computed get nameInput() {
    const {i18n} = this.context;
    const {
      customName,
      session: {
        isLoading, signupResponse,
      },
    } = this.props;


    const done = !!signupResponse;

    const input = (
      <InputField
        type="text"
        disabled={isLoading || done}
        className="form-control"
        required
        newLayout
        darkLayout
      />
    );

    const firstNameInput = React.cloneElement(input, {
      id: 'given-name',
      key: 'given-name',
      name: 'given-name',
      label: i18n._x('First Name', 'Signup Form'),
      placeholder: i18n._x('John', 'Signup Form'),
    });

    const lastNameInput = React.cloneElement(input, {
      id: 'family-name',
      key: 'family-name',
      name: 'family-name',
      label: i18n._x('Last Name', 'Signup Form'),
      placeholder: i18n._x('Smith', 'Signup Form'),
    });

    const divider = (<div key="divider" style={{marginBottom: '3rem'}} />);

    if (!customName) {
      if (i18n.umRegion === 'FR') {
        // Lastname - firstname
        return [lastNameInput, firstNameInput, divider];
      }
      // firstame - lastname
      return [firstNameInput, lastNameInput, divider];
    }

    // Some cases we already have name i.e. signup via manage/cs so we still show a single name field for those
    return React.cloneElement(input, {
      id: 'name',
      name: 'name',
      label: i18n._x('Full Name', 'Signup Form'),
      placeholder: i18n._x('John Smith', 'Signup Form'),
      defaultValue: customName,
    });
  }

  render() {
    const {i18n, cityId} = this.context;
    const {
      customEmail,
      discountId,
      hideAcceptMarketingComms,
      hideFacebookSignUp,
      location,
      selectedCity,
      session: {
        isLoading, isLoggedIn, isVerificationChallengeRequired, signupResponse,
      },
      showVoucherField,
      signupId,
      signupCampaign_id,
      trackingCategory,
    } = this.props;

    const {
      nameInput,
      newsletterCheckbox,
      onChangeCommsOptIn,
      onReceiveForm,
      passwordMinLength,
      termsCheckbox,
    } = this;

    const done = !!signupResponse;

    const {state} = location;
    let email;
    let password;
    let city;

    if (state && state.body) {
      ({
        email, password, city,
      } = this.props.location.state.body);
    }

    if (customEmail) {
      email = customEmail;
    }

    if (isVerificationChallengeRequired) {
      return (
        <VerifyAccountRequest isSignupVerification onVerifySuccess={this.onSignupSuccess} trackingCategory={trackingCategory} />
      );
    }

    return (
      <FormHandler
        method="POST"
        onReceiveForm={onReceiveForm}>
        <Form
          to={{
            pathname: location.pathname,
            query: location.query,
          }}
          method="POST"
          disabled={isLoading || done || isLoggedIn}
          className="form"
          onValidationFail={this.onValidationFail}
        >
          <div className="content--indented">
            {!hideFacebookSignUp && (
              <div>
                <LoginWithFacebook
                  label={i18n._x('Connect with Facebook', 'Signup Form')}
                  discountId={discountId}
                  signupId={signupId}
                  onLoginSuccess={this.onReceiveFacebookLogin}
                  signupCampaign_id={signupCampaign_id}
                  isSignup
                />

                <Divider className="js-only">{i18n._x('OR', 'form sections separator')}</Divider>
              </div>
            )}
            {nameInput}
            <InputField
              id="email"
              type="email"
              name="email"
              pattern="^[a-zA-Z0-9_]+[a-zA-Z0-9_\-+.=!{|}]+@[a-zA-Z0-9-]+\.[a-zA-Z-.]+$"
              disabled={isLoading || done}
              readOnly={!!customEmail}
              label={i18n._x('Email Address', 'Signup Form')}
              className="form-control"
              defaultValue={email}
              placeholder={i18n._x('john@smith.com', 'Signup Form')}
              required
              newLayout
              darkLayout
            />
            <PhoneNumberInputField
              groupClassName="signup-form__no-margin-input"
              id="telephone"
              name="telephone"
              disabled={isLoading || done}
              label={i18n._x('Mobile Number', 'Signup Form')}
              className="form-control"
              required
            />
            <p className="text-start text-info text-info--byline signup-form__field-info">
              {i18n._x('Double-check you\'ve got this right – we\'ll be texting you a code to verify your account', 'Signup form')}
            </p>
            <PasswordField
              label={i18n._x('Password', 'Signup Form')}
              id="password"
              disabled={isLoading || done}
              name="password"
              className="form-control"
              defaultValue={password}
              onChange={this.onChange}
              minLength={passwordMinLength}
              pattern="^\S*$"
              required
              newLayout
              darkLayout
            />
            {cityId ? (
              <input
                type="hidden"
                name="city"
                value={cityId}
              />
            ) : (
              <InputField
                id="city"
                label={i18n._x('Select Your City', 'Signup Form')}
                type={CitySelect}
                name="city"
                disabled={isLoading || done}
                className="form-control"
                defaultValue={city || selectedCity}
                required
                newLayout
                darkLayout
                validateOnChange
              />
            )}

            {signupCampaign_id && (
              <InputField
                id="signupCampaign_id"
                type="hidden"
                name="signupCampaign_id"
                className="form-control"
                value={signupCampaign_id}
              />
            )}

            {showVoucherField && (
              <InputField
                label={i18n._x('Voucher Code', 'Signup Form')}
                id="voucher"
                disabled={isLoading || done}
                type="text"
                name="voucher"
                className="form-control"
                required
                newLayout
                darkLayout
                defaultValue={this.voucherCodeValue}
                ref={this.voucherInputRef}
                onBlur={this.onBlurVoucher}
              />
            )}
            {(this.showAcceptTC || this.showAcceptMarketing) && (
              <div className="form-group">
                {this.showAcceptTC && (
                  <CheckboxGroup
                    items={termsCheckbox}
                    id="acceptTermsConditions"
                  />
                )}
                {!hideAcceptMarketingComms && this.showAcceptMarketing && (
                  <CheckboxGroup
                    items={newsletterCheckbox}
                    id="newsletter"
                    onChange={onChangeCommsOptIn}
                  />
                )}
                {this.acceptTermsError && (
                  <p className="signup-page__error form-field-error" data-qa="accept-terms-error">{i18n._x('You must accept the Terms & Conditions', 'Signup form')}</p>
                )}
              </div>
            )}
            <Button
              data-qa="signup-submit"
              disabled={isLoading || done}
              isLoading={isLoading}
              block
            >
              {i18n._x('Create account', 'Signup Form')}
            </Button>
          </div>
        </Form>
      </FormHandler>
    );
  }
}

export default SignupForm;



// WEBPACK FOOTER //
// ./spa/Components/Session/SignupForm.jsx